import { useState } from "react";
import { CopyIcon, CheckIcon } from "../../../components/icons";
import {
  Container,
  Header,
  Logo,
  NavLinks,
  Main,
  Sidebar,
  NavItem,
  Content,
  Section,
  EndpointCard,
  EndpointHeader,
  MethodBadge,
  EndpointPath,
  EndpointBody,
  TabsContainer,
  Tab,
  CodeBlock,
  CodeHeader,
  CopyButton,
  CodeContainer,
  LineNumbers,
  LineNumber,
  Pre,
  ParamsTable,
  RequiredBadge,
  OptionalBadge,
  Alert,
} from "./ApiDocs.style";

// Syntax highlighting function
const highlightCode = (code, language) => {
  if (!code) return "";

  // Escape HTML
  let highlighted = code
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

  // Comments (single line and multi-line)
  highlighted = highlighted.replace(
    /(\/\/.*$|\/\*[\s\S]*?\*\/|#.*$)/gm,
    '<span style="color: #8b949e; font-style: italic;">$1</span>'
  );

  // Strings (double quotes, single quotes, backticks)
  highlighted = highlighted.replace(
    /("(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*'|`(?:[^`\\]|\\.)*`)/g,
    '<span style="color: #a5d6ff;">$1</span>'
  );

  // Keywords
  const keywords = [
    "const",
    "let",
    "var",
    "function",
    "async",
    "await",
    "return",
    "if",
    "else",
    "for",
    "while",
    "switch",
    "case",
    "break",
    "continue",
    "try",
    "catch",
    "throw",
    "new",
    "class",
    "import",
    "export",
    "from",
    "default",
    "true",
    "false",
    "null",
    "undefined",
    "this",
    "using",
    "def",
    "print",
    "echo",
    "curl",
    "public",
    "private",
    "static",
    "void",
    "string",
    "int",
    "bool",
    "object",
  ];
  keywords.forEach((kw) => {
    const regex = new RegExp(`\\b(${kw})\\b`, "g");
    highlighted = highlighted.replace(
      regex,
      '<span style="color: #ff7b72;">$1</span>'
    );
  });

  // Numbers
  highlighted = highlighted.replace(
    /\b(\d+\.?\d*)\b/g,
    '<span style="color: #79c0ff;">$1</span>'
  );

  // Function calls
  highlighted = highlighted.replace(
    /\b([a-zA-Z_][a-zA-Z0-9_]*)\s*\(/g,
    '<span style="color: #d2a8ff;">$1</span>('
  );

  // Object properties (after dots)
  highlighted = highlighted.replace(
    /\.([a-zA-Z_][a-zA-Z0-9_]*)/g,
    '.<span style="color: #7ee787;">$1</span>'
  );

  // HTTP methods
  highlighted = highlighted.replace(
    /\b(GET|POST|PUT|DELETE|PATCH|HEAD|OPTIONS)\b/g,
    '<span style="color: #ff7b72; font-weight: bold;">$1</span>'
  );

  // URLs
  highlighted = highlighted.replace(
    /(https?:\/\/[^\s"'<>]+)/g,
    '<span style="color: #a5d6ff;">$1</span>'
  );

  return highlighted;
};

// Code examples for different languages
const codeExamples = {
  createContract: {
    javascript: `const response = await fetch('https://api.devcraft.io/api/v1/contracts', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'X-API-Key': 'pk_live_your_public_key',
    'X-API-Secret': 'sk_live_your_secret_key'
  },
  body: JSON.stringify({
    client: {
      name: 'John Doe',
      phone: '+972501234567',
      email: 'john@example.com'
    },
    contract: {
      variables: {
        serviceName: 'Website Development',
        price: 15000,
        paymentTerms: '50% upfront, 50% on completion'
      }
    },
    externalRef: 'deal-123',
    expiresInHours: 72
  })
});

const data = await response.json();
console.log(data.data.signingLink);`,

    python: `import requests

response = requests.post(
    'https://api.devcraft.io/api/v1/contracts',
    headers={
        'Content-Type': 'application/json',
        'X-API-Key': 'pk_live_your_public_key',
        'X-API-Secret': 'sk_live_your_secret_key'
    },
    json={
        'client': {
            'name': 'John Doe',
            'phone': '+972501234567',
            'email': 'john@example.com'
        },
        'contract': {
            'variables': {
                'serviceName': 'Website Development',
                'price': 15000,
                'paymentTerms': '50% upfront, 50% on completion'
            }
        },
        'externalRef': 'deal-123',
        'expiresInHours': 72
    }
)

data = response.json()
print(data['data']['signingLink'])`,

    curl: `curl -X POST https://api.devcraft.io/api/v1/contracts \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: pk_live_your_public_key" \\
  -H "X-API-Secret: sk_live_your_secret_key" \\
  -d '{
    "client": {
      "name": "John Doe",
      "phone": "+972501234567",
      "email": "john@example.com"
    },
    "contract": {
      "variables": {
        "serviceName": "Website Development",
        "price": 15000,
        "paymentTerms": "50% upfront, 50% on completion"
      }
    },
    "externalRef": "deal-123",
    "expiresInHours": 72
  }'`,

    php: `<?php
$ch = curl_init('https://api.devcraft.io/api/v1/contracts');

curl_setopt_array($ch, [
    CURLOPT_RETURNTRANSFER => true,
    CURLOPT_POST => true,
    CURLOPT_HTTPHEADER => [
        'Content-Type: application/json',
        'X-API-Key: pk_live_your_public_key',
        'X-API-Secret: sk_live_your_secret_key'
    ],
    CURLOPT_POSTFIELDS => json_encode([
        'client' => [
            'name' => 'John Doe',
            'phone' => '+972501234567',
            'email' => 'john@example.com'
        ],
        'contract' => [
            'variables' => [
                'serviceName' => 'Website Development',
                'price' => 15000,
                'paymentTerms' => '50% upfront, 50% on completion'
            ]
        ],
        'externalRef' => 'deal-123',
        'expiresInHours' => 72
    ])
]);

$response = curl_exec($ch);
$data = json_decode($response, true);
echo $data['data']['signingLink'];`,

    csharp: `using var client = new HttpClient();

client.DefaultRequestHeaders.Add("X-API-Key", "pk_live_your_public_key");
client.DefaultRequestHeaders.Add("X-API-Secret", "sk_live_your_secret_key");

var payload = new {
    client = new {
        name = "John Doe",
        phone = "+972501234567",
        email = "john@example.com"
    },
    contract = new {
        variables = new {
            serviceName = "Website Development",
            price = 15000,
            paymentTerms = "50% upfront, 50% on completion"
        }
    },
    externalRef = "deal-123",
    expiresInHours = 72
};

var response = await client.PostAsJsonAsync(
    "https://api.devcraft.io/api/v1/contracts", 
    payload
);

var data = await response.Content.ReadFromJsonAsync<ApiResponse>();
Console.WriteLine(data.Data.SigningLink);`,
  },

  getContract: {
    javascript: `const response = await fetch('https://api.devcraft.io/api/v1/contracts/CONTRACT_ID', {
  headers: {
    'X-API-Key': 'pk_live_your_public_key',
    'X-API-Secret': 'sk_live_your_secret_key'
  }
});

const data = await response.json();
console.log(data.data);`,

    python: `import requests

response = requests.get(
    'https://api.devcraft.io/api/v1/contracts/CONTRACT_ID',
    headers={
        'X-API-Key': 'pk_live_your_public_key',
        'X-API-Secret': 'sk_live_your_secret_key'
    }
)

data = response.json()
print(data['data'])`,

    curl: `curl https://api.devcraft.io/api/v1/contracts/CONTRACT_ID \\
  -H "X-API-Key: pk_live_your_public_key" \\
  -H "X-API-Secret: sk_live_your_secret_key"`,

    php: `<?php
$ch = curl_init('https://api.devcraft.io/api/v1/contracts/CONTRACT_ID');

curl_setopt_array($ch, [
    CURLOPT_RETURNTRANSFER => true,
    CURLOPT_HTTPHEADER => [
        'X-API-Key: pk_live_your_public_key',
        'X-API-Secret: sk_live_your_secret_key'
    ]
]);

$response = curl_exec($ch);
$data = json_decode($response, true);
print_r($data['data']);`,

    csharp: `using var client = new HttpClient();

client.DefaultRequestHeaders.Add("X-API-Key", "pk_live_your_public_key");
client.DefaultRequestHeaders.Add("X-API-Secret", "sk_live_your_secret_key");

var response = await client.GetAsync("https://api.devcraft.io/api/v1/contracts/CONTRACT_ID");
var data = await response.Content.ReadFromJsonAsync<ApiResponse>();
Console.WriteLine(data.Data);`,
  },

  webhookVerification: {
    javascript: `const crypto = require('crypto');

function verifyWebhookSignature(payload, signature, timestamp, secret) {
  const signaturePayload = \`\${timestamp}.\${JSON.stringify(payload)}\`;
  const expectedSignature = 'sha256=' + crypto
    .createHmac('sha256', secret)
    .update(signaturePayload)
    .digest('hex');
  
  return crypto.timingSafeEqual(
    Buffer.from(signature),
    Buffer.from(expectedSignature)
  );
}

// Express.js webhook handler
app.post('/webhook', express.json(), (req, res) => {
  const signature = req.headers['x-webhook-signature'];
  const timestamp = req.headers['x-webhook-timestamp'];
  
  if (!verifyWebhookSignature(req.body, signature, timestamp, WEBHOOK_SECRET)) {
    return res.status(401).json({ error: 'Invalid signature' });
  }
  
  const { event, data } = req.body;
  
  switch (event) {
    case 'contract.signed':
      console.log('Contract signed:', data.contractId);
      console.log('PDF URL:', data.documents.pdfUrl);
      break;
    case 'contract.expired':
      console.log('Contract expired:', data.contractId);
      break;
  }
  
  res.json({ received: true });
});`,

    python: `import hmac
import hashlib
from flask import Flask, request, jsonify

def verify_webhook_signature(payload, signature, timestamp, secret):
    signature_payload = f"{timestamp}.{payload}"
    expected_signature = 'sha256=' + hmac.new(
        secret.encode(),
        signature_payload.encode(),
        hashlib.sha256
    ).hexdigest()
    
    return hmac.compare_digest(signature, expected_signature)

app = Flask(__name__)

@app.route('/webhook', methods=['POST'])
def webhook():
    signature = request.headers.get('X-Webhook-Signature')
    timestamp = request.headers.get('X-Webhook-Timestamp')
    
    if not verify_webhook_signature(
        request.get_data(as_text=True), 
        signature, 
        timestamp, 
        WEBHOOK_SECRET
    ):
        return jsonify({'error': 'Invalid signature'}), 401
    
    data = request.json
    event = data['event']
    
    if event == 'contract.signed':
        print(f"Contract signed: {data['data']['contractId']}")
        print(f"PDF URL: {data['data']['documents']['pdfUrl']}")
    elif event == 'contract.expired':
        print(f"Contract expired: {data['data']['contractId']}")
    
    return jsonify({'received': True})`,

    curl: `# Webhook payload example (received from DevCraft)
{
  "event": "contract.signed",
  "timestamp": "2024-01-15T10:30:00Z",
  "data": {
    "contractId": "65abc123...",
    "externalRef": "deal-123",
    "client": {
      "name": "John Doe",
      "phone": "+972501234567"
    },
    "signature": {
      "signedAt": "2024-01-15T10:30:00Z",
      "signedFromIp": "1.2.3.4"
    },
    "documents": {
      "pdfUrl": "https://...",
      "htmlUrl": "https://..."
    }
  }
}`,

    php: `<?php
function verifyWebhookSignature($payload, $signature, $timestamp, $secret) {
    $signaturePayload = $timestamp . '.' . $payload;
    $expectedSignature = 'sha256=' . hash_hmac('sha256', $signaturePayload, $secret);
    
    return hash_equals($expectedSignature, $signature);
}

$payload = file_get_contents('php://input');
$signature = $_SERVER['HTTP_X_WEBHOOK_SIGNATURE'];
$timestamp = $_SERVER['HTTP_X_WEBHOOK_TIMESTAMP'];

if (!verifyWebhookSignature($payload, $signature, $timestamp, WEBHOOK_SECRET)) {
    http_response_code(401);
    echo json_encode(['error' => 'Invalid signature']);
    exit;
}

$data = json_decode($payload, true);

switch ($data['event']) {
    case 'contract.signed':
        error_log("Contract signed: " . $data['data']['contractId']);
        error_log("PDF URL: " . $data['data']['documents']['pdfUrl']);
        break;
    case 'contract.expired':
        error_log("Contract expired: " . $data['data']['contractId']);
        break;
}

echo json_encode(['received' => true]);`,

    csharp: `using System.Security.Cryptography;
using System.Text;

public class WebhookController : ControllerBase
{
    private bool VerifyWebhookSignature(string payload, string signature, string timestamp, string secret)
    {
        var signaturePayload = $"{timestamp}.{payload}";
        using var hmac = new HMACSHA256(Encoding.UTF8.GetBytes(secret));
        var hash = hmac.ComputeHash(Encoding.UTF8.GetBytes(signaturePayload));
        var expectedSignature = "sha256=" + BitConverter.ToString(hash).Replace("-", "").ToLower();
        
        return signature == expectedSignature;
    }

    [HttpPost("/webhook")]
    public IActionResult Webhook([FromBody] WebhookPayload payload)
    {
        var signature = Request.Headers["X-Webhook-Signature"];
        var timestamp = Request.Headers["X-Webhook-Timestamp"];
        
        if (!VerifyWebhookSignature(payload.ToString(), signature, timestamp, WEBHOOK_SECRET))
        {
            return Unauthorized(new { error = "Invalid signature" });
        }
        
        switch (payload.Event)
        {
            case "contract.signed":
                Console.WriteLine($"Contract signed: {payload.Data.ContractId}");
                Console.WriteLine($"PDF URL: {payload.Data.Documents.PdfUrl}");
                break;
            case "contract.expired":
                Console.WriteLine($"Contract expired: {payload.Data.ContractId}");
                break;
        }
        
        return Ok(new { received = true });
    }
}`,
  },
};

const CodeExample = ({ examples, defaultLang = "javascript" }) => {
  const [activeTab, setActiveTab] = useState(defaultLang);
  const [copied, setCopied] = useState(false);

  const languages = Object.keys(examples);
  const languageLabels = {
    javascript: "JavaScript",
    python: "Python",
    curl: "cURL",
    php: "PHP",
    csharp: "C#",
  };

  const copyCode = () => {
    navigator.clipboard.writeText(examples[activeTab]);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const code = examples[activeTab];
  const lines = code.split("\n");
  const highlightedCode = highlightCode(code, activeTab);

  return (
    <CodeBlock>
      <TabsContainer
        style={{ background: "#161b22", borderBottom: "1px solid #30363d" }}
      >
        {languages.map((lang) => (
          <Tab
            key={lang}
            $active={activeTab === lang}
            onClick={() => setActiveTab(lang)}
            style={{
              background: activeTab === lang ? "#0d1117" : "transparent",
              borderBottom:
                activeTab === lang
                  ? "2px solid #58a6ff"
                  : "2px solid transparent",
              color: activeTab === lang ? "#c9d1d9" : "#8b949e",
            }}
          >
            {languageLabels[lang]}
          </Tab>
        ))}
      </TabsContainer>
      <CodeHeader>
        <CopyButton onClick={copyCode}>
          {copied ? <CheckIcon /> : <CopyIcon />}
          {copied ? "Copied!" : "Copy"}
        </CopyButton>
        <span style={{ color: "#8b949e", fontSize: "12px" }}>
          {languageLabels[activeTab]}
        </span>
      </CodeHeader>
      <CodeContainer>
        <LineNumbers>
          {lines.map((_, index) => (
            <LineNumber key={index}>{index + 1}</LineNumber>
          ))}
        </LineNumbers>
        <Pre>
          <code dangerouslySetInnerHTML={{ __html: highlightedCode }} />
        </Pre>
      </CodeContainer>
    </CodeBlock>
  );
};

// Simple code block without tabs (for responses)
const SimpleCodeBlock = ({ code, language = "" }) => {
  const [copied, setCopied] = useState(false);
  const lines = code.split("\n");
  const highlightedCode = highlightCode(code, language.toLowerCase());

  const copyCode = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <CodeBlock>
      <CodeHeader>
        <CopyButton onClick={copyCode}>
          {copied ? <CheckIcon /> : <CopyIcon />}
          {copied ? "Copied!" : "Copy"}
        </CopyButton>
        {language && (
          <span style={{ color: "#8b949e", fontSize: "12px" }}>{language}</span>
        )}
      </CodeHeader>
      <CodeContainer>
        <LineNumbers>
          {lines.map((_, index) => (
            <LineNumber key={index}>{index + 1}</LineNumber>
          ))}
        </LineNumbers>
        <Pre>
          <code dangerouslySetInnerHTML={{ __html: highlightedCode }} />
        </Pre>
      </CodeContainer>
    </CodeBlock>
  );
};

const ApiDocs = () => {
  const [activeSection, setActiveSection] = useState("getting-started");

  return (
    <Container>
      <Header>
        <Logo>
          <h1>DevCraft Contracts</h1>
          <span>API v1</span>
        </Logo>
        <NavLinks>
          <a href="/company/dashboard">Dashboard</a>
          <a href="/company/settings">Get API Keys</a>
        </NavLinks>
      </Header>

      <Main>
        <Sidebar>
          <h3>Getting Started</h3>
          <NavItem
            $active={activeSection === "getting-started"}
            onClick={() => setActiveSection("getting-started")}
          >
            Introduction
          </NavItem>
          <NavItem
            $active={activeSection === "authentication"}
            onClick={() => setActiveSection("authentication")}
          >
            Authentication
          </NavItem>
          <NavItem
            $active={activeSection === "errors"}
            onClick={() => setActiveSection("errors")}
          >
            Error Handling
          </NavItem>

          <h3>Contracts</h3>
          <NavItem
            $active={activeSection === "create-contract"}
            onClick={() => setActiveSection("create-contract")}
          >
            Create Contract
          </NavItem>
          <NavItem
            $active={activeSection === "get-contract"}
            onClick={() => setActiveSection("get-contract")}
          >
            Get Contract
          </NavItem>
          <NavItem
            $active={activeSection === "list-contracts"}
            onClick={() => setActiveSection("list-contracts")}
          >
            List Contracts
          </NavItem>
          <NavItem
            $active={activeSection === "cancel-contract"}
            onClick={() => setActiveSection("cancel-contract")}
          >
            Cancel Contract
          </NavItem>
          <NavItem
            $active={activeSection === "resend-contract"}
            onClick={() => setActiveSection("resend-contract")}
          >
            Resend Contract
          </NavItem>

          <h3>Webhooks</h3>
          <NavItem
            $active={activeSection === "webhook-setup"}
            onClick={() => setActiveSection("webhook-setup")}
          >
            Webhook Setup
          </NavItem>
          <NavItem
            $active={activeSection === "webhook-events"}
            onClick={() => setActiveSection("webhook-events")}
          >
            Webhook Events
          </NavItem>
          <NavItem
            $active={activeSection === "webhook-security"}
            onClick={() => setActiveSection("webhook-security")}
          >
            Verifying Signatures
          </NavItem>

          <h3>Other</h3>
          <NavItem
            $active={activeSection === "usage"}
            onClick={() => setActiveSection("usage")}
          >
            Usage & Billing
          </NavItem>
          <NavItem
            $active={activeSection === "rate-limits"}
            onClick={() => setActiveSection("rate-limits")}
          >
            Rate Limits
          </NavItem>
        </Sidebar>

        <Content>
          {activeSection === "getting-started" && (
            <Section id="getting-started">
              <h2>Introduction</h2>
              <p>
                Welcome to the DevCraft Contracts API documentation. This API
                allows you to programmatically create, manage, and track digital
                contracts that can be signed by your clients.
              </p>

              <Alert>
                <strong>Base URL</strong>
                <p>
                  <code>https://api.devcraft.io/api/v1</code>
                </p>
              </Alert>

              <h3>Quick Start</h3>
              <p>
                To get started with the DevCraft Contracts API, you'll need to:
              </p>
              <ol style={{ color: "#94a3b8", lineHeight: 2 }}>
                <li>Sign up for a DevCraft Contracts account</li>
                <li>Get your API keys from the dashboard</li>
                <li>Make your first API call to create a contract</li>
                <li>
                  Set up a webhook to receive notifications when contracts are
                  signed
                </li>
              </ol>
            </Section>
          )}

          {activeSection === "authentication" && (
            <Section id="authentication">
              <h2>Authentication</h2>
              <p>
                All API requests require authentication using API keys. You must
                include both your public key and secret key in the request
                headers.
              </p>

              <h3>API Keys</h3>
              <p>There are two types of API keys:</p>
              <ul style={{ color: "#94a3b8", lineHeight: 2 }}>
                <li>
                  <strong>Production keys</strong> (<code>pk_live_*</code> /{" "}
                  <code>sk_live_*</code>) - Use for live contracts
                </li>
                <li>
                  <strong>Sandbox keys</strong> (<code>pk_test_*</code> /{" "}
                  <code>sk_test_*</code>) - Use for testing (no SMS sent)
                </li>
              </ul>

              <h3>Request Headers</h3>
              <ParamsTable>
                <thead>
                  <tr>
                    <th>Header</th>
                    <th>Value</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><span>X-API-Key</span></td>
                    <td><span>string</span></td>
                    <td><span>Your public API key</span></td>
                  </tr>
                  <tr>
                    <td><span>X-API-Secret</span></td>
                    <td><span>string</span></td>
                    <td><span>Your secret API key</span></td>
                  </tr>
                  <tr>
                    <td><span>Content-Type</span></td>
                    <td><span>application/json</span></td>
                    <td><span>Required for POST/PUT requests</span></td>
                  </tr>
                </tbody>
              </ParamsTable>

              <Alert $type="warning">
                <strong>Keep your secret key safe!</strong>
                <p>
                  Never expose your secret key in client-side code. Only use it
                  in server-to-server requests.
                </p>
              </Alert>
            </Section>
          )}

          {activeSection === "create-contract" && (
            <Section id="create-contract">
              <h2>Create Contract</h2>
              <p>
                Create a new contract that will be sent to the client for
                signing. An SMS with the signing link will be sent
                automatically.
              </p>

              <EndpointCard>
                <EndpointHeader>
                  <MethodBadge $method="POST">POST</MethodBadge>
                  <EndpointPath>/contracts</EndpointPath>
                </EndpointHeader>
                <EndpointBody>
                  <h4 style={{ color: "white", marginTop: 0 }}>Request Body</h4>
                  <ParamsTable>
                    <thead>
                      <tr>
                        <th>Parameter</th>
                        <th>Type</th>
                        <th>Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td><span>
                          client.name<RequiredBadge>required</RequiredBadge>
                        </span></td>
                        <td><span>string</span></td>
                        <td><span>Client's full name</span></td>
                      </tr>
                      <tr>
                        <td><span>
                          client.phone<RequiredBadge>required</RequiredBadge>
                        </span></td>
                        <td><span>string</span></td>
                        <td><span>Client's phone number (for SMS)</span></td>
                      </tr>
                      <tr>
                        <td><span>
                          client.email<OptionalBadge>optional</OptionalBadge>
                        </span></td>
                        <td><span>string</span></td>
                        <td><span>Client's email address</span></td>
                      </tr>
                      <tr>
                        <td><span>
                          contract.variables
                          <OptionalBadge>optional</OptionalBadge>
                        </span></td>
                        <td><span>object</span></td>
                        <td><span>Variables to populate the contract template</span></td>
                      </tr>
                      <tr>
                        <td><span>
                          externalRef<OptionalBadge>optional</OptionalBadge>
                        </span></td>
                        <td><span>string</span></td>
                        <td><span>Your reference ID (returned in webhooks)</span></td>
                      </tr>
                      <tr>
                        <td><span>
                          expiresInHours<OptionalBadge>optional</OptionalBadge>
                        </span></td>
                        <td><span>number</span></td>
                        <td><span>Hours until contract expires (default: 72)</span></td>
                      </tr>
                      <tr>
                        <td><span>
                          metadata<OptionalBadge>optional</OptionalBadge>
                        </span></td>
                        <td><span>object</span></td>
                        <td><span>Custom data (returned in webhooks)</span></td>
                      </tr>
                    </tbody>
                  </ParamsTable>
                </EndpointBody>
              </EndpointCard>

              <h3>Example Request</h3>
              <CodeExample examples={codeExamples.createContract} />

              <h3>Response</h3>
              <SimpleCodeBlock
                language="JSON"
                code={`{
  "success": true,
  "data": {
    "contractId": "65abc123def456789",
    "signingLink": "https://sign.devcraft.io/s/xyz789abc",
    "expiresAt": "2024-01-18T10:30:00Z",
    "status": "PENDING",
    "smsSent": true,
    "isSandbox": false
  }
}`}
              />
            </Section>
          )}

          {activeSection === "get-contract" && (
            <Section id="get-contract">
              <h2>Get Contract</h2>
              <p>
                Retrieve details about a specific contract, including its
                current status and download links for signed documents.
              </p>

              <EndpointCard>
                <EndpointHeader>
                  <MethodBadge $method="GET">GET</MethodBadge>
                  <EndpointPath>/contracts/:contractId</EndpointPath>
                </EndpointHeader>
              </EndpointCard>

              <h3>Example Request</h3>
              <CodeExample examples={codeExamples.getContract} />

              <h3>Response</h3>
              <SimpleCodeBlock
                language="JSON"
                code={`{
  "success": true,
  "data": {
    "contractId": "65abc123def456789",
    "externalRef": "deal-123",
    "client": {
      "name": "John Doe",
      "phone": "+972501234567",
      "email": "john@example.com"
    },
    "status": "SIGNED",
    "createdAt": "2024-01-15T10:30:00Z",
    "expiresAt": "2024-01-18T10:30:00Z",
    "signature": {
      "signedAt": "2024-01-15T14:22:00Z",
      "signedFromIp": "1.2.3.4"
    },
    "documents": {
      "pdfUrl": "https://s3.../contract.pdf?signature=...",
      "htmlUrl": "https://s3.../contract.html?signature=..."
    }
  }
}`}
              />
            </Section>
          )}

          {activeSection === "webhook-events" && (
            <Section id="webhook-events">
              <h2>Webhook Events</h2>
              <p>
                DevCraft Contracts sends webhook notifications when important
                events occur. Configure your webhook URL in the dashboard to
                receive these events.
              </p>

              <h3>Available Events</h3>
              <ParamsTable>
                <thead>
                  <tr>
                    <th>Event</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><span>contract.created</span></td>
                    <td><span>A new contract was created</span></td>
                  </tr>
                  <tr>
                    <td><span>contract.viewed</span></td>
                    <td><span>The client opened the contract link</span></td>
                  </tr>
                  <tr>
                    <td><span>contract.signed</span></td>
                    <td><span>The client signed the contract (includes PDF link)</span></td>
                  </tr>
                  <tr>
                    <td><span>contract.expired</span></td>
                    <td><span>The contract expired without being signed</span></td>
                  </tr>
                  <tr>
                    <td><span>contract.cancelled</span></td>
                    <td><span>The contract was cancelled</span></td>
                  </tr>
                </tbody>
              </ParamsTable>
            </Section>
          )}

          {activeSection === "webhook-security" && (
            <Section id="webhook-security">
              <h2>Verifying Webhook Signatures</h2>
              <p>
                All webhook requests include a signature that you should verify
                to ensure the request came from DevCraft Contracts.
              </p>

              <h3>Signature Headers</h3>
              <ParamsTable>
                <thead>
                  <tr>
                    <th>Header</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><span>X-Webhook-Signature</span></td>
                    <td><span>HMAC-SHA256 signature of the payload</span></td>
                  </tr>
                  <tr>
                    <td><span>X-Webhook-Timestamp</span></td>
                    <td><span>Unix timestamp when the webhook was sent</span></td>
                  </tr>
                </tbody>
              </ParamsTable>

              <h3>Verification Example</h3>
              <CodeExample examples={codeExamples.webhookVerification} />

              <Alert $type="warning">
                <strong>Important</strong>
                <p>
                  Always verify webhook signatures before processing the
                  payload. This prevents attackers from sending fake webhook
                  requests to your server.
                </p>
              </Alert>
            </Section>
          )}

          {activeSection === "errors" && (
            <Section id="errors">
              <h2>Error Handling</h2>
              <p>
                The API uses standard HTTP status codes to indicate success or
                failure. Error responses include a JSON body with details.
              </p>

              <h3>Error Response Format</h3>
              <SimpleCodeBlock
                language="JSON"
                code={`{
  "success": false,
  "error": {
    "code": "VALIDATION_ERROR",
    "message": "Client phone number is required"
  }
}`}
              />

              <h3>HTTP Status Codes</h3>
              <ParamsTable>
                <thead>
                  <tr>
                    <th>Status</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><span>200</span></td>
                    <td><span>Success</span></td>
                  </tr>
                  <tr>
                    <td><span>201</span></td>
                    <td><span>Created successfully</span></td>
                  </tr>
                  <tr>
                    <td><span>400</span></td>
                    <td><span>Bad request - invalid parameters</span></td>
                  </tr>
                  <tr>
                    <td><span>401</span></td>
                    <td><span>Unauthorized - invalid API keys</span></td>
                  </tr>
                  <tr>
                    <td><span>403</span></td>
                    <td><span>Forbidden - subscription inactive or limit reached</span></td>
                  </tr>
                  <tr>
                    <td><span>404</span></td>
                    <td><span>Not found</span></td>
                  </tr>
                  <tr>
                    <td><span>429</span></td>
                    <td><span>Rate limit exceeded</span></td>
                  </tr>
                  <tr>
                    <td><span>500</span></td>
                    <td><span>Internal server error</span></td>
                  </tr>
                </tbody>
              </ParamsTable>
            </Section>
          )}

          {activeSection === "rate-limits" && (
            <Section id="rate-limits">
              <h2>Rate Limits</h2>
              <p>
                API requests are rate limited based on your subscription plan.
                Rate limit information is included in response headers.
              </p>

              <h3>Rate Limit Headers</h3>
              <ParamsTable>
                <thead>
                  <tr>
                    <th>Header</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><span>X-RateLimit-Limit</span></td>
                    <td><span>Maximum requests per minute</span></td>
                  </tr>
                  <tr>
                    <td><span>X-RateLimit-Remaining</span></td>
                    <td><span>Remaining requests in current window</span></td>
                  </tr>
                  <tr>
                    <td><span>X-RateLimit-Reset</span></td>
                    <td><span>When the rate limit resets (ISO timestamp)</span></td>
                  </tr>
                </tbody>
              </ParamsTable>

              <h3>Default Limits</h3>
              <ParamsTable>
                <thead>
                  <tr>
                    <th>Plan</th>
                    <th>Requests/Minute</th>
                    <th>Requests/Hour</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><span>Starter</span></td>
                    <td><span>60</span></td>
                    <td><span>1,000</span></td>
                  </tr>
                  <tr>
                    <td><span>Professional</span></td>
                    <td><span>120</span></td>
                    <td><span>5,000</span></td>
                  </tr>
                  <tr>
                    <td><span>Enterprise</span></td>
                    <td><span>300</span></td>
                    <td><span>10,000</span></td>
                  </tr>
                </tbody>
              </ParamsTable>
            </Section>
          )}

          {activeSection === "list-contracts" && (
            <Section id="list-contracts">
              <h2>List Contracts</h2>
              <p>
                Retrieve a paginated list of all contracts for your company. You
                can filter by status, date range, and search by client name or
                phone.
              </p>

              <EndpointCard>
                <EndpointHeader>
                  <MethodBadge $method="GET">GET</MethodBadge>
                  <EndpointPath>/contracts</EndpointPath>
                </EndpointHeader>
                <EndpointBody>
                  <h4 style={{ color: "white", marginTop: 0 }}>
                    Query Parameters
                  </h4>
                  <ParamsTable>
                    <thead>
                      <tr>
                        <th>Parameter</th>
                        <th>Type</th>
                        <th>Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td><span>
                          status<OptionalBadge>optional</OptionalBadge>
                        </span></td>
                        <td><span>string</span></td>
                        <td><span>
                          Filter by status: PENDING, VIEWED, SIGNED, EXPIRED,
                          CANCELLED
                        </span></td>
                      </tr>
                      <tr>
                        <td><span>
                          search<OptionalBadge>optional</OptionalBadge>
                        </span></td>
                        <td><span>string</span></td>
                        <td><span>Search by client name or phone</span></td>
                      </tr>
                      <tr>
                        <td><span>
                          fromDate<OptionalBadge>optional</OptionalBadge>
                        </span></td>
                        <td><span>string</span></td>
                        <td><span>
                          Filter contracts created after this date (ISO 8601)
                        </span></td>
                      </tr>
                      <tr>
                        <td><span>
                          toDate<OptionalBadge>optional</OptionalBadge>
                        </span></td>
                        <td><span>string</span></td>
                        <td><span>
                          Filter contracts created before this date (ISO 8601)
                        </span></td>
                      </tr>
                      <tr>
                        <td><span>
                          page<OptionalBadge>optional</OptionalBadge>
                        </span></td>
                        <td><span>number</span></td>
                        <td><span>Page number (default: 1)</span></td>
                      </tr>
                      <tr>
                        <td><span>
                          limit<OptionalBadge>optional</OptionalBadge>
                        </span></td>
                        <td><span>number</span></td>
                        <td><span>Items per page (default: 20, max: 100)</span></td>
                      </tr>
                    </tbody>
                  </ParamsTable>
                </EndpointBody>
              </EndpointCard>

              <h3>Response</h3>
              <SimpleCodeBlock
                language="JSON"
                code={`{
  "success": true,
  "data": {
    "contracts": [
      {
        "contractId": "65abc123def456789",
        "externalRef": "deal-123",
        "client": {
          "name": "John Doe",
          "phone": "+972501234567"
        },
        "status": "SIGNED",
        "createdAt": "2024-01-15T10:30:00Z",
        "signedAt": "2024-01-15T14:22:00Z"
      }
    ],
    "pagination": {
      "page": 1,
      "limit": 20,
      "total": 45,
      "pages": 3
    }
  }
}`}
              />
            </Section>
          )}

          {activeSection === "cancel-contract" && (
            <Section id="cancel-contract">
              <h2>Cancel Contract</h2>
              <p>
                Cancel a pending contract. This will prevent the client from
                signing it and trigger a <code>contract.cancelled</code> webhook
                event.
              </p>

              <EndpointCard>
                <EndpointHeader>
                  <MethodBadge $method="POST">POST</MethodBadge>
                  <EndpointPath>/contracts/:contractId/cancel</EndpointPath>
                </EndpointHeader>
                <EndpointBody>
                  <h4 style={{ color: "white", marginTop: 0 }}>
                    Request Body (Optional)
                  </h4>
                  <ParamsTable>
                    <thead>
                      <tr>
                        <th>Parameter</th>
                        <th>Type</th>
                        <th>Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td><span>
                          reason<OptionalBadge>optional</OptionalBadge>
                        </span></td>
                        <td><span>string</span></td>
                        <td><span>Reason for cancellation (for your records)</span></td>
                      </tr>
                    </tbody>
                  </ParamsTable>
                </EndpointBody>
              </EndpointCard>

              <Alert $type="warning">
                <strong>Note</strong>
                <p>
                  Only contracts with status PENDING or VIEWED can be cancelled.
                  Signed contracts cannot be cancelled.
                </p>
              </Alert>

              <h3>Response</h3>
              <SimpleCodeBlock
                language="JSON"
                code={`{
  "success": true,
  "data": {
    "contractId": "65abc123def456789",
    "status": "CANCELLED",
    "cancelledAt": "2024-01-16T09:00:00Z",
    "reason": "Client requested cancellation"
  }
}`}
              />
            </Section>
          )}

          {activeSection === "resend-contract" && (
            <Section id="resend-contract">
              <h2>Resend Contract</h2>
              <p>
                Resend the SMS with the signing link to the client. Useful when
                the client hasn't received the original message or needs a
                reminder.
              </p>

              <EndpointCard>
                <EndpointHeader>
                  <MethodBadge $method="POST">POST</MethodBadge>
                  <EndpointPath>/contracts/:contractId/resend</EndpointPath>
                </EndpointHeader>
              </EndpointCard>

              <Alert $type="warning">
                <strong>Rate Limited</strong>
                <p>
                  You can only resend the SMS once every 5 minutes per contract
                  to prevent spam.
                </p>
              </Alert>

              <h3>Response</h3>
              <SimpleCodeBlock
                language="JSON"
                code={`{
  "success": true,
  "data": {
    "contractId": "65abc123def456789",
    "smsSent": true,
    "sentTo": "+972501234567",
    "sentAt": "2024-01-16T10:00:00Z"
  }
}`}
              />
            </Section>
          )}

          {activeSection === "webhook-setup" && (
            <Section id="webhook-setup">
              <h2>Webhook Setup</h2>
              <p>
                Webhooks allow your application to receive real-time
                notifications when events occur in DevCraft Contracts. Configure
                your webhook URL in the dashboard.
              </p>

              <h3>Configuration Steps</h3>
              <ol style={{ color: "#94a3b8", lineHeight: 2 }}>
                <li>
                  Go to <strong>Settings → Webhooks</strong> in your dashboard
                </li>
                <li>Enter your webhook endpoint URL (must be HTTPS)</li>
                <li>
                  Enable the webhook and select which events you want to receive
                </li>
                <li>Copy your webhook secret for signature verification</li>
                <li>Send a test webhook to verify your endpoint is working</li>
              </ol>

              <h3>Webhook Payload Format</h3>
              <p>
                All webhook requests are sent as POST with the following format:
              </p>

              <SimpleCodeBlock
                language="JSON"
                code={`{
  "event": "contract.signed",
  "timestamp": "2024-01-15T14:22:00Z",
  "webhookId": "wh_abc123",
  "data": {
    "contractId": "65abc123def456789",
    "externalRef": "deal-123",
    "client": {
      "name": "John Doe",
      "phone": "+972501234567",
      "email": "john@example.com"
    },
    "status": "SIGNED",
    "signature": {
      "signedAt": "2024-01-15T14:22:00Z",
      "signedFromIp": "1.2.3.4"
    },
    "documents": {
      "pdfUrl": "https://s3.../signed-contract.pdf?signature=...",
      "htmlUrl": "https://s3.../signed-contract.html?signature=..."
    },
    "metadata": {
      "customField": "your-custom-data"
    }
  }
}`}
              />

              <h3>Webhook Headers</h3>
              <ParamsTable>
                <thead>
                  <tr>
                    <th>Header</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><span>Content-Type</span></td>
                    <td><span>application/json</span></td>
                  </tr>
                  <tr>
                    <td><span>X-Webhook-Signature</span></td>
                    <td><span>HMAC-SHA256 signature for verification</span></td>
                  </tr>
                  <tr>
                    <td><span>X-Webhook-Timestamp</span></td>
                    <td><span>Unix timestamp when sent</span></td>
                  </tr>
                  <tr>
                    <td><span>X-Webhook-Id</span></td>
                    <td><span>Unique ID for this webhook delivery</span></td>
                  </tr>
                </tbody>
              </ParamsTable>

              <h3>Retry Policy</h3>
              <p>
                If your endpoint returns a non-2xx status code, we will retry
                the webhook with exponential backoff:
              </p>
              <ul style={{ color: "#94a3b8", lineHeight: 2 }}>
                <li>1st retry: after 1 minute</li>
                <li>2nd retry: after 5 minutes</li>
                <li>3rd retry: after 30 minutes</li>
                <li>4th retry: after 2 hours</li>
                <li>5th retry: after 24 hours</li>
              </ul>
              <p>After 5 failed attempts, the webhook is marked as failed.</p>
            </Section>
          )}

          {activeSection === "usage" && (
            <Section id="usage">
              <h2>Usage & Billing</h2>
              <p>Track your API usage and understand how billing works.</p>

              <h3>Check Usage</h3>
              <EndpointCard>
                <EndpointHeader>
                  <MethodBadge $method="GET">GET</MethodBadge>
                  <EndpointPath>/usage</EndpointPath>
                </EndpointHeader>
              </EndpointCard>

              <h3>Response</h3>
              <SimpleCodeBlock
                language="JSON"
                code={`{
  "success": true,
  "data": {
    "subscription": {
      "plan": "professional",
      "status": "active",
      "currentPeriodStart": "2024-01-01T00:00:00Z",
      "currentPeriodEnd": "2024-02-01T00:00:00Z"
    },
    "usage": {
      "contractsCreated": 127,
      "contractsSigned": 98,
      "contractsExpired": 15,
      "contractsCancelled": 14
    },
    "limits": {
      "contractsPerMonth": 500,
      "remainingContracts": 373
    },
    "billing": {
      "pricePerDocument": 5,
      "currency": "ILS",
      "documentsIncluded": 100,
      "documentsUsed": 127,
      "extraDocuments": 27,
      "currentAmountDue": 135
    }
  }
}`}
              />

              <h3>Billing Model</h3>
              <p>DevCraft Contracts uses a pay-per-document billing model:</p>
              <ul style={{ color: "#94a3b8", lineHeight: 2 }}>
                <li>Each plan includes a number of documents per month</li>
                <li>
                  Additional documents are billed at your plan's per-document
                  rate
                </li>
                <li>
                  Only <strong>created</strong> contracts count toward your
                  usage (not views or signatures)
                </li>
                <li>Sandbox/test contracts do not count toward billing</li>
                <li>Usage resets at the start of each billing period</li>
              </ul>

              <Alert>
                <strong>Pro Tip</strong>
                <p>
                  Use sandbox API keys for testing to avoid charges. Sandbox
                  contracts behave exactly like production but don't send real
                  SMS messages.
                </p>
              </Alert>
            </Section>
          )}
        </Content>
      </Main>
    </Container>
  );
};

export default ApiDocs;
